import { useCallback, useEffect, useRef, useState } from "react";
import type { Lang, Media } from "../types.ts";
import { t } from "../i18n/ui.ts";
import { mediaUrl } from "../media.ts";

/** The real exam lets you watch each film five times. */
export const MAX_PLAYS = 5;

interface Props {
  media: Media;
  /** Question number, used for the accessible description. */
  number: string;
  lang: Lang;
  /** Bumped by the parent to trigger playback from a keyboard shortcut. */
  playSignal?: number;
  /** Fired once a film has been watched through to the end. */
  onWatched?: () => void;
}

/** Safari exposes fullscreen for video under its own vendor-prefixed names. */
type FullscreenVideo = HTMLVideoElement & {
  webkitEnterFullscreen?: () => void;
  webkitRequestFullscreen?: () => void;
};

/**
 * Hazard-perception films run under exam rules: five viewings, no scrubbing,
 * no pausing, and full-screen while they play. A bare <video> with our own
 * start button is the only way to get that — the browser's control bar exists
 * to offer exactly the scrub handle we have to withhold.
 */
export function MediaView({ media, number, lang, playSignal = 0, onWatched }: Props) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [failed, setFailed] = useState(false);
  const [plays, setPlays] = useState(0);
  const [playing, setPlaying] = useState(false);
  /** Furthest point reached, so a seek forward can be undone. */
  const watched = useRef(0);

  const exhausted = plays >= MAX_PLAYS;

  /**
   * Full-screen, by whatever name the browser knows it. iPhone has no
   * Element.requestFullscreen at all — only HTMLVideoElement carries its own
   * webkitEnterFullscreen — so the standard call is tried first and the
   * vendor one is both a fallback and the rejection handler.
   */
  const enterFullscreen = useCallback((el: FullscreenVideo) => {
    const vendor = () => {
      if (el.webkitEnterFullscreen) el.webkitEnterFullscreen();
      else if (el.webkitRequestFullscreen) el.webkitRequestFullscreen();
    };

    // webkitEnterFullscreen is a no-op before metadata has arrived.
    if (!el.requestFullscreen && el.readyState < 1) {
      el.addEventListener("loadedmetadata", vendor, { once: true });
      return;
    }
    if (el.requestFullscreen) void el.requestFullscreen().catch(vendor);
    else vendor();
  }, []);

  const start = useCallback(() => {
    const el = videoRef.current as FullscreenVideo | null;
    if (!el || plays >= MAX_PLAYS) return;
    setPlays((n) => n + 1);
    watched.current = 0;
    el.currentTime = 0;
    // Both calls stay synchronous inside the click, or the browser stops
    // treating them as user-initiated and refuses each on its own grounds.
    void el.play().catch(() => {});
    enterFullscreen(el);
  }, [plays, enterFullscreen]);

  // The V shortcut starts a run, and is subject to the same limit.
  useEffect(() => {
    if (playSignal > 0) start();
    // start() changes with `plays`; re-running on that would replay the film.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playSignal]);

  // iOS leaves its fullscreen player on its own terms; stop the film with it
  // rather than letting it run on invisibly behind the page.
  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    const onExit = () => {
      if (!el.ended) el.pause();
      setPlaying(false);
    };
    el.addEventListener("webkitendfullscreen", onExit);
    return () => el.removeEventListener("webkitendfullscreen", onExit);
  }, []);

  // A new question resets the allowance.
  useEffect(() => {
    setPlays(0);
    setPlaying(false);
    watched.current = 0;
  }, [media.file]);

  if (failed) {
    return (
      <p className="media media--missing" role="status">
        {t(lang, "mediaUnavailable")} ({media.file})
      </p>
    );
  }

  if (media.kind === "video") {
    return (
      <figure className="media media--video">
        <video
          ref={videoRef}
          src={mediaUrl(media)}
          preload="metadata"
          playsInline
          onError={() => setFailed(true)}
          onPlay={() => setPlaying(true)}
          onTimeUpdate={(e) => {
            watched.current = Math.max(watched.current, e.currentTarget.currentTime);
          }}
          onPause={(e) => {
            // No pausing mid-run. Where we own the UI there is no pause
            // control at all, but iOS puts its own on the fullscreen player,
            // so a pause that is not the end of the clip resumes itself.
            const el = e.currentTarget;
            const inFullscreen =
              document.fullscreenElement === el ||
              (el as FullscreenVideo & { webkitDisplayingFullscreen?: boolean })
                .webkitDisplayingFullscreen === true;
            if (!el.ended && playing && inFullscreen) {
              void el.play().catch(() => setPlaying(false));
            }
          }}
          onSeeking={(e) => {
            // Anything past what has actually been watched snaps back.
            const el = e.currentTarget;
            if (el.currentTime > watched.current + 0.35) el.currentTime = watched.current;
          }}
          onEnded={() => {
            setPlaying(false);
            if (document.fullscreenElement) void document.exitFullscreen().catch(() => {});
            onWatched?.();
          }}
          onDoubleClick={(e) => e.preventDefault()}
          aria-label={`${t(lang, "videoQuestion")} ${number}`}
        />

        {!playing && (
          <div className="media__controls">
            <button
              type="button"
              className="btn btn--primary media__btn"
              onClick={start}
              disabled={exhausted}
            >
              {plays === 0 ? t(lang, "startFilm") : t(lang, "replayFilm")}
            </button>
          </div>
        )}

        <figcaption>
          {exhausted
            ? t(lang, "filmPlaysUsedUp")
            : t(lang, "filmPlaysLeft", { n: MAX_PLAYS - plays })}
        </figcaption>
      </figure>
    );
  }

  return (
    <figure className="media media--image">
      <img
        src={mediaUrl(media)}
        alt=""
        loading="lazy"
        onError={() => setFailed(true)}
      />
    </figure>
  );
}
